import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, getStatusBadge } from '@/backoffice/components/ui';
import { fetchDriverDocuments, type DriverDocument } from '@/domains/driver/services/driver.service';
import { Check, Clock, AlertCircle, FileText } from 'lucide-react';

export function ProDocumentsPage() {
  const [documents, setDocuments] = useState<DriverDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDriverDocuments()
      .then(setDocuments)
      .finally(() => setLoading(false));
  }, []);

  const validCount = documents.filter((d) => d.status === 'valid').length;
  const pendingCount = documents.filter((d) => d.status === 'pending').length;

  return (
    <DashboardLayout userType="pro_driver" title="Documents">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-3">
            <Check className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="text-sm text-emerald-700">Validés</p>
              <p className="text-xl font-semibold text-emerald-800">{validCount}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm text-amber-700">En attente</p>
              <p className="text-xl font-semibold text-amber-800">{pendingCount}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm text-blue-700">Total</p>
              <p className="text-xl font-semibold text-blue-800">{documents.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-8">Chargement...</p>
      ) : documents.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucun document enregistré</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <Card key={doc.id}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">{doc.category?.name ?? `Catégorie #${doc.category_id}`}</p>
                    <p className="text-sm text-gray-500">{doc.file_url}</p>
                    <p className="text-xs text-gray-400">
                      {doc.started_at} → {doc.ended_at}
                    </p>
                  </div>
                </div>
                {getStatusBadge(doc.status)}
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
